import axios, { type AxiosResponse } from 'axios';
import { logger } from '@trading-utils/logger';

import { processBackendError } from '@/utils/error.utils';

import i18n from '@/i18n/config';

import { apiRoutes } from '@/config/apiRoutes';

import type { Company, Plant, CreatePlantPayload, CreateCompanyUserPayload, User } from '@/interfaces';

export const getCompanyDetails = async (companyId: number): Promise<Company> => {
  const url = `${apiRoutes.base}${apiRoutes.company.base}/${companyId}`;

  try {
    const response: AxiosResponse<Company> = await axios.get(url);

    logger.info(`Fetched company details for company ${companyId}`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching company details', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorGetCompanyDetails'));
  }
};

export const createPlant = async (payload: CreatePlantPayload): Promise<Plant> => {
  const url = `${apiRoutes.base}${apiRoutes.plants.base}`;

  try {
    const response: AxiosResponse<Plant> = await axios.post(url, {
      identifier: payload.identifier,
      alias: payload.alias,
      usersIds: payload.users.map((user) => user.id),
      companyId: payload.companyId,
      pricingIndexId: payload.pricingIndexId ?? null,
    });

    logger.info('Plant created successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error creating plant', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorCreatePlant'));
  }
};

export const updatePlant = async (id: number, payload: Partial<CreatePlantPayload>): Promise<Plant> => {
  const url = `${apiRoutes.base}${apiRoutes.plants.base}/${id}`;

  try {
    const response: AxiosResponse<Plant> = await axios.put(url, {
      identifier: payload.identifier,
      alias: payload.alias,
      usersIds: payload.users?.map((user) => user.id),
      companyId: payload.companyId,
      pricingIndexId: payload.pricingIndexId ?? null,
    });

    logger.info('Plant updated successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error updating plant', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorUpdatePlant'));
  }
};

export const deletePlant = async (id: number): Promise<Plant> => {
  const url = `${apiRoutes.base}${apiRoutes.plants.base}/${id}`;

  try {
    const response: AxiosResponse<Plant> = await axios.delete(url);

    logger.info('Plant deleted successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error deleting plant', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorDeletePlant'));
  }
};

export const createCompanyUser = async (payload: CreateCompanyUserPayload): Promise<User> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}`;

  try {
    const response: AxiosResponse<User> = await axios.post(url, {
      name: payload.name,
      email: payload.email,
      role: payload.role,
      companyId: payload.companyId,
      plantIds: payload.plants.map((plant) => plant.id),
    });

    logger.info('User created successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error creating user', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorCreateCompanyUser'));
  }
};

export const updateCompanyUser = async (id: number, payload: Partial<CreateCompanyUserPayload>): Promise<User> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${id}`;

  try {
    const response: AxiosResponse<User> = await axios.put(url, {
      name: payload.name,
      role: payload.role,
      plantIds: payload.plants?.map((plant) => plant.id),
    });

    logger.info('User updated successfully', { url, response: response.data });

    return response.data;
  } catch (error) {
    logger.error('Error updating user', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorUpdateCompanyUser'));
  }
};

export const deleteCompanyUser = async (userId: number, companyId: number): Promise<void> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${userId}/companies/${companyId}`;

  try {
    await axios.delete(url);

    logger.info(`User ${userId} removed from company ${companyId}`, { url });
  } catch (error) {
    logger.error('Error removing user from company', { url, error });
    throw processBackendError(error, i18n.t('errors.companyDetails.errorDeleteCompanyUser'));
  }
};

/**
 * Get the companies associated with a user
 * GET /user/:userId/companies
 */
export const getUserCompanies = async (userId: number): Promise<Company[]> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${userId}/companies`;

  try {
    const response: AxiosResponse<Company[]> = await axios.get(url);

    logger.info(`Fetched ${response.data.length} companies for user ${userId}`);

    return response.data;
  } catch (error) {
    logger.error('Error fetching user companies', { url, error });
    throw processBackendError(error, i18n.t('errors.userCompanies.errorGetUserCompanies'));
  }
};

/**
 * Update the active company for a user
 * PATCH /user/:userId/companies
 */
export const updateActiveCompany = async (userId: number, activeCompanyId: number): Promise<void> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${userId}/companies`;

  try {
    await axios.patch(url, { activeCompanyId });

    logger.info(`Updated active company for user ${userId} to ${activeCompanyId}`);
  } catch (error) {
    logger.error('Error updating active company', { url, error });
    throw processBackendError(error, i18n.t('errors.userCompanies.errorUpdateActiveCompany'));
  }
};

/**
 * Add a company to a user
 * POST /user/:userId/companies/:companyId
 */
export const addUserCompany = async (userId: number, companyId: number): Promise<void> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${userId}/companies/${companyId}`;

  try {
    await axios.post(url);

    logger.info(`Added company ${companyId} to user ${userId}`);
  } catch (error) {
    logger.error('Error adding company to user', { url, error });
    throw processBackendError(error, i18n.t('errors.userCompanies.errorAddUserCompany'));
  }
};

/**
 * Remove a company from a user
 * DELETE /user/:userId/companies/:companyId
 */
export const removeUserCompany = async (userId: number, companyId: number): Promise<void> => {
  const url = `${apiRoutes.base}${apiRoutes.users.base}/${userId}/companies/${companyId}`;

  try {
    await axios.delete(url);

    logger.info(`Removed company ${companyId} from user ${userId}`);
  } catch (error) {
    logger.error('Error removing company from user', { url, error });
    throw processBackendError(error, i18n.t('errors.userCompanies.errorRemoveUserCompany'));
  }
};
